import React, { useState, useEffect } from 'react';
import { SPM10_BUDGET_STATS, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Clock, Play, Pause, AlertTriangle, ShieldCheck } from 'lucide-react';

interface SPM10CarbonBudgetProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM10CarbonBudget: React.FC<SPM10CarbonBudgetProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm10'];
  const audioEngine = AudioEngine.getInstance();

  const [consumedBudget, setConsumedBudget] = useState<number>(0); // 0 to 500 GtCO2

  // Auto-countdown loop when playing
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setConsumedBudget((prev) => {
          const next = prev + 20;
          if (next > 500) return 0;
          return next;
        });
      }, 400);
    } else {
      audioEngine.stopAnthropoPulse();
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  // Update Audio based on remaining budget percentage
  useEffect(() => {
    if (!audioEngine.getIsUnlocked() || !isPlaying) return;

    const remaining = 500 - consumedBudget;
    const remainingPercent = remaining / 500; // 1.0 down to 0.0

    // Pitch rises as budget shrinks
    const freq = audioEngine.tempToFreq(1.5 + (1 - remainingPercent) * 1.5);
    // Harmonics strip away (FM index reduces from 8 down to 0.5 as budget depletes)
    const fmIndex = Math.max(0.5, remainingPercent * 8);

    audioEngine.startAnthropoPulse(freq, fmIndex, 0.4);
  }, [consumedBudget, isPlaying]);

  const remainingGt = 500 - consumedBudget;
  const yearsLeftAt2021Rate = (remainingGt / SPM10_BUDGET_STATS.annualEmissions2021).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Countdown' : 'Start Budget Countdown'}</span>
        </button>
      </div>

      {/* Main Countdown Visualizer */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-slate-400 block font-semibold">1.5°C Total Allowance (from 2020)</span>
            <span className="text-3xl font-extrabold font-mono text-cyan-400">
              500 <span className="text-xs text-slate-400 font-sans">GtCO2</span>
            </span>
            <span className="text-[10px] text-slate-500 block">50% probability target</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-slate-400 block font-semibold">Allowance Consumed</span>
            <span className="text-3xl font-extrabold font-mono text-amber-400">
              {consumedBudget} <span className="text-xs text-slate-400 font-sans">GtCO2</span>
            </span>
            <span className="text-[10px] text-slate-500 block">Simulated timeline progress</span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-xs font-mono text-slate-400 block font-semibold">Remaining Allowance</span>
            <span className="text-3xl font-extrabold font-mono text-red-400">
              {remainingGt} <span className="text-xs text-slate-400 font-sans">GtCO2</span>
            </span>
            <span className="text-[10px] text-slate-500 block">~{yearsLeftAt2021Rate} years at 2021 emissions rate</span>
          </div>
        </div>

        {/* Dynamic Budget Consumption Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>500 GtCO2 Remaining</span>
            <span className="text-red-400 font-bold">
              {((consumedBudget / 500) * 100).toFixed(0)}% Consumed (Upper Harmonics Stripped)
            </span>
            <span>0 GtCO2 (Exhausted)</span>
          </div>

          <div className="w-full bg-slate-950 h-8 rounded-2xl border border-slate-800 p-1 overflow-hidden relative">
            <div
              className="h-full rounded-xl bg-gradient-to-r from-cyan-500 via-amber-500 to-red-600 transition-all duration-300 shadow-inner"
              style={{ width: `${(consumedBudget / 500) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Timeline Scrubber */}
        <input
          type="range"
          min="0"
          max="500"
          step="10"
          value={consumedBudget}
          onChange={(e) => setConsumedBudget(parseInt(e.target.value))}
          className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
        />
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
